import { useToast } from "@ligo/layout-design-core";
import { Errors } from "classes";
import { allQuestionsKey, ss } from "consts";
import { QuestionModel } from "models";
import {
  AttBaseProviderType,
  PermissionAttProviderType,
  useAttendDetail,
} from "providers";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import * as AttendanceService from "services/attendance";
import { aytyFormatError } from "utils";

type QuestionContextType = {
  loading: boolean;
  question?: QuestionModel;
  getQuestion: (isReload?: boolean) => Promise<void>;
  getPreviousQuestion: () => Promise<void>;
};

const QuestionContext = createContext({} as QuestionContextType);

const Provider = ({ children, all }: AttBaseProviderType) => {
  const [loading, setLoading] = useState(true);
  const [question, setQuestion] = useState<QuestionModel>();
  const { pIdOmni } = useAttendDetail();
  const { error, warning } = useToast();
  const { t } = useTranslation();

  const errorsResolver = useMemo(
    () => new Errors({ error, warning }, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (pIdOmni) {
      setLoading(true);
      setQuestion(undefined);
    }
  }, [pIdOmni]);

  const getQuestion = useCallback(
    async (isReload?: boolean) => {
      let current: QuestionModel = all[pIdOmni];

      if (isReload || !current)
        await AttendanceService.getQuestion({ pIdOmni })
          .then(async ({ data }) => {
            if (data.idReturnAPI > 0) {
              const { idReturnAPI, deReturnAPI, dtServer, ...args } = data;
              current = args;
              all[pIdOmni] = args;
            } else warning({ description: t(aytyFormatError(data)) });
          })
          .catch(errorsResolver.defaultError);

      setQuestion(current);
      ss.setItem(allQuestionsKey, JSON.stringify(all));

      setLoading(false);
    },
    [all, pIdOmni, errorsResolver, warning, t]
  );

  const getPreviousQuestion = useCallback(async () => {
    let current: QuestionModel = all[pIdOmni];

    await AttendanceService.previousQuestion({ pIdOmni })
      .then(async ({ data }) => {
        if (data.idReturnAPI > 0) {
          const { idReturnAPI, deReturnAPI, dtServer, ...args } = data;
          current = args;
          all[pIdOmni] = args;
        } else warning({ description: t(aytyFormatError(data)) });
      })
      .catch(errorsResolver.defaultError);

    setQuestion(current);
    ss.setItem(allQuestionsKey, JSON.stringify(all));
  }, [all, pIdOmni, errorsResolver, warning, t]);

  return (
    <QuestionContext.Provider
      value={{ loading, question, getQuestion, getPreviousQuestion }}
    >
      {children}
    </QuestionContext.Provider>
  );
};

export const QuestionProvider = ({
  hasPermission = true,
  ...args
}: PermissionAttProviderType) => {
  if (!hasPermission) return <>{args.children}</>;

  return <Provider {...args} />;
};

export const useQuestion = () => useContext(QuestionContext);
